/**
 * Payment Service
 * Handles payment-related API calls
 */

import { apiClient } from "../client";
import type {
  CreatePaymentOrderRequest,
  CreatePaymentOrderResponse,
  PaymentOrderDetailsResponse,
  TransactionsListResponse,
} from "../types";

export const paymentService = {
  /**
   * Create payment order
   * Endpoint: Payment/create-order
   */
  async createOrder(data: CreatePaymentOrderRequest): Promise<CreatePaymentOrderResponse> {
    await apiClient.loadConfig();
    
    console.log("=== Payment Service: Creating Order ===");
    console.log("Request URL:", `${apiClient.getBaseURL()}/Payment/create-order`);
    console.log("Request Data:", JSON.stringify(data, null, 2));
    
    const response = await apiClient.post<CreatePaymentOrderResponse>("Payment/create-order", data);
    
    console.log("=== Payment Service: API Response ===");
    console.log("Full Response Object:", response);
    console.log("Response Data:", response.data);
    console.log("Response Success:", response.success);
    console.log("Response Message:", response.message);
    
    // The API client wraps the response, so we need to check the structure
    // If response.data is the actual response object, return it
    // Otherwise, the response itself might be the data
    let finalResponse: CreatePaymentOrderResponse;
    
    if (response.data && typeof response.data === 'object') {
      const responseData = response.data as any;
      // Check if response.data has the expected structure
      if ('paymentSessionId' in responseData || 'success' in responseData) {
        finalResponse = responseData as CreatePaymentOrderResponse;
      } else if (responseData.data) {
        // If response.data is wrapped, try to access it
        finalResponse = responseData.data as CreatePaymentOrderResponse;
      } else {
        // Fallback: return the response as-is
        finalResponse = responseData as CreatePaymentOrderResponse;
      }
    } else {
      // Fallback: return the response as-is
      finalResponse = response.data as CreatePaymentOrderResponse;
    }
    
    console.log("=== Payment Service: Final Processed Response ===");
    console.log("Final Response:", JSON.stringify(finalResponse, null, 2));
    console.log("Payment Session ID:", finalResponse?.paymentSessionId);
    console.log("Order ID:", finalResponse?.orderId);
    console.log("Payment Link:", finalResponse?.paymentLink);
    
    return finalResponse;
  },

  /**
   * Get payment order details by orderId
   * Endpoint: Payment/order/{orderId}
   */
  async getOrderByOrderId(orderId: string): Promise<PaymentOrderDetailsResponse> {
    await apiClient.loadConfig();
    
    console.log("=== Payment Service: Getting Order Details ===");
    console.log("Request URL:", `${apiClient.getBaseURL()}/Payment/order/${orderId}`);
    
    const response = await apiClient.get<PaymentOrderDetailsResponse>(`Payment/order/${orderId}`);
    
    console.log("=== Payment Service: Order Details Response ===");
    console.log("Full Response:", response);
    
    // The API client wraps the response, so we need to check the structure
    let finalResponse: PaymentOrderDetailsResponse;
    
    if (response.data && typeof response.data === 'object') {
      const responseData = response.data as any;
      if ('orderId' in responseData || 'id' in responseData) {
        finalResponse = responseData as PaymentOrderDetailsResponse;
      } else if (responseData.data) {
        finalResponse = responseData.data as PaymentOrderDetailsResponse;
      } else {
        finalResponse = responseData as PaymentOrderDetailsResponse;
      }
    } else {
      finalResponse = response.data as PaymentOrderDetailsResponse;
    }
    
    console.log("=== Payment Service: Final Processed Response ===");
    console.log("Final Response:", JSON.stringify(finalResponse, null, 2));
    
    return finalResponse;
  },

  /**
   * Get transactions list
   * Endpoint: Payment/transactions-list
   */
  async getTransactionsList(params: {
    firmId: number;
    fromDate: string;
    toDate: string;
    status: string;
  }): Promise<TransactionsListResponse> {
    await apiClient.loadConfig();
    
    console.log("=== Payment Service: Getting Transactions List ===");
    console.log("Request URL:", `${apiClient.getBaseURL()}/Payment/transactions-list`);
    console.log("Request Params:", JSON.stringify(params, null, 2));
    
    const response = await apiClient.get<TransactionsListResponse>("Payment/transactions-list", params);
    
    console.log("=== Payment Service: Transactions List Response ===");
    console.log("Full Response:", response);
    
    // Handle array response
    let finalResponse: TransactionsListResponse;
    
    if (Array.isArray(response.data)) {
      finalResponse = response.data as TransactionsListResponse;
    } else if (response.data && typeof response.data === 'object' && Array.isArray((response.data as any).data)) {
      finalResponse = (response.data as any).data as TransactionsListResponse;
    } else {
      finalResponse = [];
    }
    
    console.log("=== Payment Service: Final Processed Response ===");
    console.log("Final Response:", JSON.stringify(finalResponse, null, 2));
    console.log("Transactions Count:", finalResponse.length);
    
    return finalResponse;
  },
};

